import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useScanMonitor } from '../hooks/useScanMonitor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from './ui/dialog';
import {
  Upload,
  FileText,
  X,
  AlertCircle,
  Download,
  Package,
  FolderOpen,
  Github
} from 'lucide-react';
import { ScanAPI, Scan } from '../api/scans';
import { ProjectAPI } from '../api/projects';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/index';
import { getStatusColor, isSubscriptionValid } from '@/helpers/common';
import { useAuth } from '@/contexts/AuthContext';
import { PackageLimitsAPI } from '@/api/package-limits';
import { SCAN_RESULT_ACCEPT, isAcceptedScanResultFile, validateScanResultFile } from '@/helpers/scanResultUpload';

interface UploadOrtFilesProps {
  isOpen: boolean;
  onClose: () => void;
  scan: Scan | null;
  onSuccess?: () => void;
}

export function UploadOrtFiles({ isOpen, onClose, scan, onSuccess }: UploadOrtFilesProps) {
  const { subscription } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGitHubAuthenticated, setIsGitHubAuthenticated] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLogs, setUploadLogs] = useState<{ message: string; timestamp: number }[]>([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastProcessedProgress, setLastProcessedProgress] = useState<{
    scanId: number;
    progress: number;
    status: string;
    message?: string;
    timestamp: number;
  } | null>(null);
  const { isConnected, scanProgress, subscribeTo, unsubscribeFrom } = useScanMonitor();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasInitializedAuthOnOpenRef = useRef(false);
  const navigate = useNavigate();
  const [scanResults, setScanResults] = useState<{
    summary: {
      totalComponents: number;
      totalEncryptionComponents: number;
      totalLicenseIssues: number;
      vulnerabilitiesFound: number;
      exploitableVulnerabilities: number;
      provenance: number;
    };
    riskScores: {
      businessRisk: number;
      totalTechnicalDebt: number;
      overallRiskScore: number;
      operationalRiskScore: number;
      vulnerabilityRiskScore: number;
      licenseRiskScore: number;
    };
    reports: {
      ortReportUrl?: string;
      sccReportUrl?: string;
    };
  } | null>(null);

  const handleFileSelect = (file: File) => {
    if (!isAcceptedScanResultFile(file.name)) {
      toast.error('Please select a ZIP, JSON (CycloneDX SBOM / SPDX) or YAML (SPDX) file');
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 100MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !scan) {
      toast.error('Please select a file');
      return;
    }

    const validationError = await validateScanResultFile(selectedFile);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // const isSubscriptionActive = isSubscriptionValid(subscription?.validupto || null);
    // if (!isSubscriptionActive) {
    //   toast.error('Your subscription has expired. Please renew to upload ORT files.');
    //   return;
    // }

    try {
      const validationResponse = await PackageLimitsAPI.validateOrtUploadCreation();
      if (!validationResponse.data?.canCreate) {
        toast.error(validationResponse.data?.message || 'You have reached your ORT file upload limit. Please upgrade your plan.');
        return;
      }
    } catch (error) {
      toast.error('Unable to validate ORT upload limits. Please try again.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const initialLogs = [{ message: 'Preparing to upload ORT files...', timestamp: Date.now() }];
    setUploadLogs(initialLogs);
    updateScanlogInDatabase(initialLogs);

    setIsSubscribed(false);
    setLastProcessedProgress(null);

    try {
      setUploadLogs(prev => {
        const updatedLogs = [...prev, { message: `Uploading ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)...`, timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs);
        return updatedLogs;
      });

      if (scan.id) {
        subscribeTo(scan.id);
        setIsSubscribed(true);
        setUploadLogs(prev => {
          const updatedLogs = [...prev, { message: '📡 Real-time monitoring activated', timestamp: Date.now() }];
          updateScanlogInDatabase(updatedLogs);
          return updatedLogs;
        });
      }

      const result = await ScanAPI.uploadOrtFiles(scan.id, selectedFile);

      if (String(result.code??"") === "200") {
        setUploadLogs(prev => {
          const updatedLogs = [...prev, { message: 'Upload successful! Processing results...', timestamp: Date.now() }];
          updateScanlogInDatabase(updatedLogs);
          return updatedLogs;
        });

        setSelectedFile(null);

        if (onSuccess) {
          onSuccess();
        }
      } else if (String(result.code??"") === "500") {
         setUploadLogs(prev => {
          const updatedLogs = [...prev, { message: `Upload failed: ${result.message || 'Unknown error'}`, timestamp: Date.now() }];
          updateScanlogInDatabase(updatedLogs);
          return updatedLogs;
        });
        toast.error('Failed to upload ORT files. Please try again.');
        setIsUploading(false);
        setIsSubscribed(false);
        if (scan.id) {
          unsubscribeFrom(scan.id);
        }
      

      } else {
       
         const isAuthError = result.error?.includes('GitHub authentication failed') ||
          result.error?.includes('Invalid GitHub access token') ||
          result.error?.includes('401') ||
          result.error?.includes('Unauthorized') ||
          result.error?.includes('Bad credentials');
        if (isAuthError) {
          setIsGitHubAuthenticated(false);
          setUploadLogs(prev => {
            const updatedLogs = [
              ...prev,
              { message: '🔐 GitHub authentication failed. Please re-authenticate to continue.', timestamp: Date.now() }
            ];
            updateScanlogInDatabase(updatedLogs);
            return updatedLogs;
          });
          toast.error('GitHub authentication failed. Please click "Authenticate with GitHub" to continue.', {
            duration: 3000,
          });
        } else {
          setUploadLogs(prev => {
            const updatedLogs = [...prev, { message: `Upload failed: ${result.message || 'Unknown error'}`, timestamp: Date.now() }];
            updateScanlogInDatabase(updatedLogs);
            return updatedLogs;
          });
          toast.error('Failed to upload ORT files. Please try again.');
        }
        setIsUploading(false);
        setIsSubscribed(false);
        if (scan.id) {
          unsubscribeFrom(scan.id);
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setUploadLogs(prev => {
        const updatedLogs = [...prev, { message: `Upload failed: ${errorMessage}`, timestamp: Date.now() }];
        updateScanlogInDatabase(updatedLogs);
        return updatedLogs;
      });
      toast.error('Failed to upload ORT files. Please try again.');
      setIsUploading(false);
      setIsSubscribed(false);
      if (scan.id) {
        unsubscribeFrom(scan.id);
      }
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setShowResults(false);
      setScanResults(null);
      setUploadProgress(0);
      setUploadLogs([]);
      if (scan?.id) {
        updateScanlogInDatabase([]);
      }
      setIsSubscribed(false);
      if (scan?.id) {
        unsubscribeFrom(scan.id);
      }
      onClose();
    }
  };

  const updateScanlogInDatabase = async (logs: Array<{ message: string, timestamp: number }>) => {
    if (scan?.id && logs.length > 0) {
      try {
        const scanlogContent = logs.map(log => `[${new Date(log.timestamp).toLocaleString()}] ${log.message}`).join('\n');
        await ScanAPI.update(scan.id, {
          scanlog: scanlogContent
        });
      } catch (error) {
        console.error('Failed to update scanlog in database:', error);
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatProjectSize = (sizeInBytes: number): string => {
    if (sizeInBytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
    return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };




  const saveGithubCredentialsToProject = async (githubData: any) => {
    try {


      await ProjectAPI.updateRepoFromProfile(scan.projectid, {
        repoid: githubData.repoid,
      });


      toast.success('GitHub credentials saved to project successfully!');


    } catch (error) {
      toast.error('Failed to save GitHub credentials to project');
      throw error;
    }
  };

  const initiateGithubOAuth = () => {

    const state = btoa(JSON.stringify({
      action: 'connect_project',
      projectId: scan.projectid,
      returnTo: 'scan',
      saveToProject: true
    }));


    const popup = window.open(
      `${api.auth.getGitHubAuthUrl()}&state=${encodeURIComponent(state)}`,
      'github-oauth-connect',
      'width=600,height=700,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,status=no'
    );

    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'GITHUB_OAUTH_SUCCESS') {
        popup?.close();
        window.removeEventListener('message', messageListener);

        if (event.data.data && event.data.data.data) {
          const connectResult = event.data.data.data;

          if (connectResult.connected) {
            saveGithubCredentialsToProject({ repoid: connectResult.repoid })
              .then(() => {
                toast.success('GitHub credentials saved to project!');
                setIsGitHubAuthenticated(true);
              })
              .catch((error) => {
                toast.error('Failed to save credentials to project');
              });
          } else {
            toast.error('GitHub account could not be connected');
          }
        } else {
          toast.success('GitHub authentication successful!');
          setIsGitHubAuthenticated(true);
        }
      } else if (event.data.type === 'GITHUB_OAUTH_ERROR') {
        popup?.close();
        toast.error(`GitHub authentication failed: ${event.data.error}`);
        window.removeEventListener('message', messageListener);
      }
    };

    window.addEventListener('message', messageListener);

    const checkPopup = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkPopup);
        window.removeEventListener('message', messageListener);
      }
    }, 1000);
  };

  const fetchScanResults = async (scanId: number) => {
    try {
      const scanResponse = await ScanAPI.getById(scanId);

      if (scanResponse.data) {
        const scan = scanResponse.data;

        const results = {
          summary: {
            totalComponents: scan.componentsfound || 0,
            totalEncryptionComponents: scan.encryption_count || 0,
            totalLicenseIssues: scan.licenseissuesfound || 0,
            vulnerabilitiesFound: scan.vulnerabilitiesfound || 0,
            exploitableVulnerabilities: scan.exploitable_vulnerabilities || 0,
            provenance: scan.provenance_count || 0,
          },
          riskScores: {
            businessRisk: scan.businessriskscore || 0,
            totalTechnicalDebt: scan.technicaldebtscore || 0,
            overallRiskScore: scan.overallscore || 0,
            operationalRiskScore: scan.operationalriskscore || 0,
            vulnerabilityRiskScore: scan.vulnerabilityscore || 0,
            licenseRiskScore: scan.licensescore || 0,
          },
          reports: {
            ortReportUrl: `/api/scans/${scanId}/download/ort`,
            sccReportUrl: `/reports/scc/${scanId}`,
          }
        };

        setScanResults(results);
        return results;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      hasInitializedAuthOnOpenRef.current = false;
      return;
    }

    // if (!hasInitializedAuthOnOpenRef.current) {
    //   setIsGitHubAuthenticated(scan?.has_github_authentication ?? false);
    //   hasInitializedAuthOnOpenRef.current = true;
    // }
  }, [isOpen, scan?.has_github_authentication]);

  useEffect(() => {
    if (scan?.id && scanProgress[scan.id] && isSubscribed) {
      const progress = scanProgress[scan.id];
      const currentTimestamp = Date.now();

      const progressKey = `${scan.id}-${progress.progress}-${progress.status}-${progress.message || ''}`;

      if (
        lastProcessedProgress &&
        lastProcessedProgress.scanId === scan.id &&
        lastProcessedProgress.progress === progress.progress &&
        lastProcessedProgress.status === progress.status &&
        (currentTimestamp - lastProcessedProgress.timestamp) < 1000
      ) {
        return;
      }

      setLastProcessedProgress({
        scanId: scan.id,
        progress: progress.progress,
        status: progress.status,
        message: progress.message,
        timestamp: currentTimestamp
      });

      const progressMessage = `📡 ${progress.message || `${progress.status} - ${progress.progress}%`}`;

      setUploadProgress(progress.progress);
      setUploadLogs(prev => {
        const updatedLogs = [
          ...prev,
          { message: progressMessage, timestamp: currentTimestamp }
        ];
        updateScanlogInDatabase(updatedLogs);
        return updatedLogs;
      });

      if (progress.status === 'completed') {
        setIsUploading(false);
        setIsSubscribed(false);

        setUploadLogs(prev => {
          const updatedLogs = [
            ...prev,
            { message: 'Upload completed! Fetching scan results...', timestamp: Date.now() }
          ];
          updateScanlogInDatabase(updatedLogs);
          return updatedLogs;
        });

        setTimeout(async () => {
          try {
            const fetchedResults = await fetchScanResults(scan.id);

            if (fetchedResults && fetchedResults.summary.totalComponents >= 0) {
              setShowResults(true);
              toast.success('Upload completed successfully!');
              setUploadLogs(prev => {
                const updatedLogs = [
                  ...prev,
                  { message: 'Scan results loaded successfully!', timestamp: Date.now() }
                ];
                updateScanlogInDatabase(updatedLogs);
                return updatedLogs;
              });
            } else {
              setUploadLogs(prev => {
                const updatedLogs = [
                  ...prev,
                  { message: 'Results not ready yet, retrying...', timestamp: Date.now() }
                ];
                updateScanlogInDatabase(updatedLogs);
                return updatedLogs;
              });

              setTimeout(async () => {
                try {
                  const retryResults = await fetchScanResults(scan.id);
                  setShowResults(true);
                  toast.success('Upload completed successfully!');

                  if (retryResults && retryResults.summary.totalComponents > 0) {
                    setUploadLogs(prev => {
                      const updatedLogs = [
                        ...prev,
                        { message: 'Scan results loaded successfully!', timestamp: Date.now() }
                      ];
                      updateScanlogInDatabase(updatedLogs);
                      return updatedLogs;
                    });
                  } else {
                    setUploadLogs(prev => {
                      const updatedLogs = [
                        ...prev,
                        { message: 'Upload completed (scan may have no components)', timestamp: Date.now() }
                      ];
                      updateScanlogInDatabase(updatedLogs);
                      return updatedLogs;
                    });
                  }
                } catch (error) {
                  setShowResults(true);
                  toast.success('Upload completed successfully!');
                  setUploadLogs(prev => {
                    const updatedLogs = [
                      ...prev,
                      { message: 'Upload completed. Some results may be delayed.', timestamp: Date.now() }
                    ];
                    updateScanlogInDatabase(updatedLogs);
                    return updatedLogs;
                  });
                }
              }, 3000);
            }
          } catch (error) {
            setShowResults(true);
            toast.success('Upload completed successfully!');
            setUploadLogs(prev => {
              const updatedLogs = [
                ...prev,
                { message: 'Upload completed. Results may take a moment to appear.', timestamp: Date.now() }
              ];
              updateScanlogInDatabase(updatedLogs);
              return updatedLogs;
            });
          }
        }, 2000);

      } else if (progress.status === 'failed' || progress.status === 'cancelled') {
        setIsUploading(false);
        setIsSubscribed(false);

        const message = progress.error || `Upload ${progress.status.toLowerCase()}`;
        toast.error(`❌ ${message}`);

        setUploadLogs(prev => {
          const updatedLogs = [
            ...prev,
            { message: `❌ ${message}`, timestamp: Date.now() }
          ];
          updateScanlogInDatabase(updatedLogs);
          return updatedLogs;
        });
      }
    }
  }, [scanProgress, scan?.id, isSubscribed, unsubscribeFrom]);

  useEffect(() => {
    if (!isOpen && scan?.id && isSubscribed) {
      unsubscribeFrom(scan.id);
      setIsSubscribed(false);
    }
   
  }, [isOpen, scan?.id, isSubscribed, unsubscribeFrom]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={"md:max-w-4xl max-h-[90vh] overflow-y-auto"}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Upload className="w-5 h-5" />
              <span>{showResults ? 'Upload Complete - Scan Results' : 'Upload ORT Files'}</span>
            </div>

          </DialogTitle>

        </DialogHeader>

        {showResults && scanResults ? (
          <div className="space-y-6">
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-3">Scan Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <Card className="bg-blue-50/10 border-blue-200/50 dark:bg-blue-500/10 dark:border-blue-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {scanResults?.summary.totalComponents.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-blue-700 dark:text-blue-300">Total Components</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-cyan-50/10 border-cyan-200/50 dark:bg-cyan-500/10 dark:border-cyan-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                        {scanResults?.summary.totalEncryptionComponents.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-cyan-700 dark:text-cyan-300">Total Encryption Algorithms</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50/10 border-amber-200/50 dark:bg-amber-500/10 dark:border-amber-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                        {scanResults?.summary.totalLicenseIssues.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-amber-700 dark:text-amber-300">Total License Issues</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50/10 border-red-200/50 dark:bg-red-500/10 dark:border-red-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-red-600 dark:text-red-400">
                        {scanResults?.summary.vulnerabilitiesFound.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-red-700 dark:text-red-300">Vulnerabilities Found</div>
                    </CardContent>
                  </Card>
                  {/* <Card className="bg-rose-50/10 border-rose-200/50 dark:bg-rose-500/10 dark:border-rose-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-rose-600 dark:text-rose-400">
                        {scanResults?.summary.exploitableVulnerabilities.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-rose-700 dark:text-rose-300">Exploitable Vulnerabilities</div>
                    </CardContent>
                  </Card> */}
                  <Card className="bg-green-50/10 border-green-200/50 dark:bg-green-500/10 dark:border-green-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-green-600 dark:text-green-400">
                        {scanResults?.summary.provenance.toLocaleString() || '0'}
                      </div>
                      <div className="text-xs text-green-700 dark:text-green-300">Provenance</div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Risk Assessment Scores</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* <Card className="bg-blue-50/10 border-blue-200/50 dark:bg-blue-500/10 dark:border-blue-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {Number(scanResults?.riskScores.businessRisk)?.toFixed(2) || '0.0'}
                      </div>
                      <div className="text-xs text-blue-700 dark:text-blue-300">Business Risk</div>
                    </CardContent>
                  </Card> */}
                  <Card className="bg-red-50/10 border-red-200/50 dark:bg-red-500/10 dark:border-red-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-red-600 dark:text-red-400">
                        {Number(scanResults?.riskScores.overallRiskScore)?.toFixed(2) || '0.0'}
                      </div>
                      <div className="text-xs text-red-700 dark:text-red-300">Overall Risk Score</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50/10 border-purple-200/50 dark:bg-purple-500/10 dark:border-purple-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                        {Number(scanResults?.riskScores.totalTechnicalDebt)?.toFixed(2) || '0.0'}
                      </div>
                      <div className="text-xs text-purple-700 dark:text-purple-300">Total Technical Debt</div>
                    </CardContent>
                  </Card>

                  <Card className="bg-indigo-50/10 border-indigo-200/50 dark:bg-indigo-500/10 dark:border-indigo-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                        {Number(scanResults?.riskScores.operationalRiskScore)?.toFixed(2) || '0.0'}
                      </div>
                      <div className="text-xs text-indigo-700 dark:text-indigo-300">Operational Risk Score</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50/10 border-orange-200/50 dark:bg-orange-500/10 dark:border-orange-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                        {Number(scanResults?.riskScores.vulnerabilityRiskScore)?.toFixed(2) || '0.0'}
                      </div>
                      <div className="text-xs text-orange-700 dark:text-orange-300">Vulnerability Risk Score</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-yellow-50/10 border-yellow-200/50 dark:bg-yellow-500/10 dark:border-yellow-400/20">
                    <CardContent className="p-4 text-center">
                      <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                        {Number(scanResults?.riskScores.licenseRiskScore)?.toFixed(2) || '0.0'}
                      </div>
                      <div className="text-xs text-yellow-700 dark:text-yellow-300">License Risk Score</div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Available Reports</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium">ORT Report</h5>
                        <Badge variant="outline">Multiple Formats</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Detailed OSS Review Toolkit report with vulnerability details
                      </p>
                      <Button
                        variant="outline"
                        className="w-full rounded-lg"
                        onClick={async () => {
                          if (scan?.id) {
                            const loadingToast = toast.loading('Generating ORT report... This may take a few moments.');
                            setIsDownloading(true);
                            try {
                              await ScanAPI.downloadOrtReport(scan.id);
                              toast.dismiss(loadingToast);
                              toast.success('ORT report downloaded successfully');
                            } catch (error) {
                              toast.dismiss(loadingToast);
                              toast.error('Failed to download ORT report. Please try again.');
                            } finally {
                              setIsDownloading(false);
                            }
                          }
                        }}
                        disabled={!scan?.id || isDownloading}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {isDownloading ? 'Downloading...' : 'Download ORT Report'}
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium">SCC Report</h5>
                        <Badge variant="outline">Interactive</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Interactive Source Code Control dashboard with detailed analysis
                      </p>
                      <Button
                        variant="outline"
                        className="w-full rounded-lg"

                        onClick={() => navigate('/component-view-report/' + scan.id)}
                        disabled={!scanResults?.reports.sccReportUrl}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        View SCC Report
                      </Button>
                    </CardContent>
                  </Card>

                  {scan && scanProgress[scan.id] && scanProgress[scan.id].workflowUrl && (
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium">GitHub Actions Raw Log</h5>
                          <Badge variant="outline">External</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          View detailed GitHub Actions workflow execution logs
                        </p>
                        <Button
                          variant="outline"
                          className="w-full rounded-lg"
                          onClick={() => {
                            const workflowUrl = scanProgress[scan.id].workflowUrl;
                            if (workflowUrl) {
                              window.open(workflowUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          <Github className="w-4 h-4 mr-2" />
                          View GitHub Actions Log
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {scan && (
              <div className="bg-muted/50 p-3 rounded-lg dark:bg-muted/30">
                <div className="mb-1">{`${scan.code} # ${scan.version ?? ""}`}</div>
                <div className="flex items-center space-x-2 flex-shrink-0 mb-1">

                  <div className="text-center">
                    <Badge variant="outline" className="bg-muted" title='Application Name'>
                      <Package className="w-4 h-4 mr-1" />
                      {scan.application_name}
                    </Badge>
                  </div>
                  <div className="text-center">
                    <Badge variant="outline" className="bg-muted" title='Project Name'>
                      <FolderOpen className="w-4 h-4 mr-1" />
                      {scan.project_name}
                    </Badge>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">

                  <Badge className={getStatusColor(scan?.scanstatus) + " mr-1"} variant="outline">
                    {scan?.scanstatus}
                  </Badge>
                  <Badge className={getStatusColor(scan?.status)} variant="outline">
                    {scan?.status}
                  </Badge>
                </div>

              </div>

            )}


            <div className="space-y-3">
              <Label htmlFor="ortFile">Select ORT ZIP, CycloneDX JSON or SPDX JSON/YAML File</Label>

              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragOver
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-500/10 dark:border-blue-400/50'
                  : 'border-muted-foreground/30 hover:border-muted-foreground/50 dark:border-muted-foreground/20 dark:hover:border-muted-foreground/40'
                  }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center space-x-2">
                      <FileText className="w-8 h-8 text-green-500" />
                      <div>
                        <div className="font-medium">{selectedFile.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatFileSize(selectedFile.size)}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedFile(null)}
                        className="ml-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">
                        Drag and drop your ORT ZIP, CycloneDX JSON or SPDX JSON/YAML file here, or{' '}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                          browse
                        </button>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Maximum file size: 100MB
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                id="ortFile"
                type="file"
                accept={SCAN_RESULT_ACCEPT}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            </div>

            <div className="bg-blue-50/10 border border-blue-200/50 dark:bg-blue-500/10 dark:border-blue-400/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <div className="font-medium mb-1">Upload Requirements:</div>
                  <ul className="space-y-1 text-xs">
                    <li>• File must be a ZIP (ORT scan results), a standalone CycloneDX JSON SBOM, or an SPDX 2.x document (JSON or YAML)</li>
                    <li>• ZIP should contain ORT scan results and reports</li>
                    <li>• Maximum file size is 100MB</li>
                    <li>• This will replace any existing ORT files for this scan</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg dark:bg-muted/30">
              <h4 className="font-medium mb-3 flex items-center">

                Upload Progress
              </h4>

              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">

                  <span className="text-muted-foreground">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>

              <div className="bg-slate-900 dark:bg-slate-950 text-green-400 p-4 rounded-lg font-mono text-sm max-h-60 overflow-y-auto">
                <div className="text-xs space-y-1 font-mono">
                  {uploadLogs.map((log, index) => (
                    <div key={index} className="mb-1">
                      <span className="text-slate-500 dark:text-slate-400">[{new Date(log.timestamp).toLocaleTimeString()}]</span> {log.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end space-x-2 pt-4">
          <DialogClose asChild>
            <Button
              variant="outline"
              disabled={isUploading}
              className="rounded-lg"
            >
              {showResults ? 'Close' : 'Cancel'}
            </Button>
          </DialogClose>
          {!showResults && !isGitHubAuthenticated && (
            <Button
              onClick={initiateGithubOAuth}
              variant="outline"
              className="rounded-lg text-orange-600 border-orange-200 hover:bg-orange-50"
            >
              Connect GitHub
            </Button>
          )}
          {!showResults && isGitHubAuthenticated && (
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="rounded-lg"
            >
              {isUploading ? (
                <>
                  <Upload className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Files
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}